import { forwardRef, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { toPng } from 'html-to-image'
import QRCode from 'qrcode'
import type { GeometryResponse, RoutePublic } from '@xingroute/schema'
import type { TFunction } from 'i18next'
import type { MessageKey } from '../i18n/index.js'
import { useLocale } from '../locale/LocaleContext.js'
import { enumDisplay } from '../lib/enumDisplay.js'
import { trackStats } from '../lib/gpx.js'
import { safeDownloadBasename, triggerHrefDownload } from '../lib/kml.js'
import { Button } from './ui/Button.js'
import { Input, Select } from './ui/Field.js'
import { buildRouteStaticMapUrl } from './RouteThumbMap.js'

export type SharePreset = 'xiaohongshu' | 'wechat' | 'weibo'

const CARD_W = 1080
const CARD_H = 1350
const PREVIEW_SCALE = 0.32

const PRESETS: Record<
  SharePreset,
  {
    labelKey: MessageKey
    footerKey: MessageKey
  }
> = {
  xiaohongshu: {
    labelKey: 'share.preset.xiaohongshu.label',
    footerKey: 'share.preset.xiaohongshu.footer',
  },
  wechat: {
    labelKey: 'share.preset.wechat.label',
    footerKey: 'share.preset.wechat.footer',
  },
  weibo: {
    labelKey: 'share.preset.weibo.label',
    footerKey: 'share.preset.weibo.footer',
  },
}

interface ShareRouteModalProps {
  route: RoutePublic
  geometry: GeometryResponse | null
  shareUrl: string
  onClose: () => void
}

export function ShareRouteModal({ route, geometry, shareUrl, onClose }: ShareRouteModalProps) {
  const { locale, t } = useLocale()
  const [preset, setPreset] = useState<SharePreset>('xiaohongshu')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 260,
      color: { dark: '#1a5a44', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [shareUrl])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function downloadImage() {
    if (!cardRef.current) return
    setExporting(true)
    setError(null)
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#f6f8f6',
      })
      triggerHrefDownload(
        dataUrl,
        `xingroute-${safeDownloadBasename(route.slug, 'route')}-${preset}.png`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setExporting(false)
    }
  }

  const presetLabel = t(PRESETS[preset].labelKey)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-3"
      role="dialog"
      aria-modal="true"
      aria-label={t('aria.shareRoute')}
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="grid max-h-[94svh] w-full max-w-5xl gap-4 overflow-y-auto rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-[var(--xr-shadow-md)] lg:grid-cols-[minmax(0,1fr)_320px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[var(--xr-text-primary)]">
                {locale === 'zh' ? '分享线路' : 'Share route'}
              </h2>
              <p className="text-xs text-[var(--xr-text-muted)]">
                {locale === 'zh'
                  ? '同一张 1080×1350 卡片，按平台切换页脚文案。'
                  : 'One 1080×1350 frame; presets only change the footer copy.'}
              </p>
            </div>
            <Button variant="ghost" size="sm" type="button" onClick={onClose}>
              {locale === 'zh' ? '关闭' : 'Close'}
            </Button>
          </div>

          <div
            className="mx-auto overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-canvas)] shadow-[var(--xr-shadow-sm)]"
            style={{ width: CARD_W * PREVIEW_SCALE, height: CARD_H * PREVIEW_SCALE }}
          >
            <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }}>
              <ShareCard
                ref={cardRef}
                route={route}
                geometry={geometry}
                qrDataUrl={qrDataUrl}
                shareUrl={shareUrl}
                preset={preset}
              />
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <label className="block">
            <span
              id="share-preset-label"
              className="mb-1 block text-sm font-medium text-[var(--xr-text-primary)]"
            >
              {t('share.presetLabel')}
            </span>
            <Select
              id="share-preset"
              name="share-preset"
              aria-labelledby="share-preset-label"
              value={preset}
              onChange={(e) => setPreset(e.target.value as SharePreset)}
            >
              {(Object.keys(PRESETS) as SharePreset[]).map((key) => (
                <option key={key} value={key}>
                  {t(PRESETS[key].labelKey)}
                </option>
              ))}
            </Select>
          </label>

          <div>
            <label
              htmlFor="share-link"
              className="mb-1 block text-sm font-medium text-[var(--xr-text-primary)]"
            >
              {t('share.linkLabel')}
            </label>
            <div className="flex gap-2">
              <Input
                id="share-link"
                name="share-link"
                readOnly
                value={shareUrl}
                className="min-w-0 flex-1"
              />
              <Button variant="secondary" size="sm" type="button" onClick={() => void copyLink()}>
                {copied
                  ? locale === 'zh'
                    ? '已复制'
                    : 'Copied'
                  : locale === 'zh'
                    ? '复制'
                    : 'Copy'}
              </Button>
            </div>
          </div>

          <div>
            <span className="mb-2 block text-sm font-medium text-[var(--xr-text-primary)]">
              {locale === 'zh' ? '二维码' : 'QR code'}
            </span>
            <div className="flex items-center gap-3 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] p-3">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt=""
                  className="h-24 w-24 rounded-[var(--xr-radius-sm)] bg-white"
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-[var(--xr-radius-sm)] bg-[var(--xr-bg-surface)] text-xs text-[var(--xr-text-muted)]">
                  …
                </div>
              )}
              <p className="text-xs text-[var(--xr-text-secondary)]">
                {locale === 'zh'
                  ? `当前 preset：${presetLabel}。二维码会随 PNG 一起导出。`
                  : `Current preset: ${presetLabel}. The QR code is exported into the PNG.`}
              </p>
            </div>
          </div>

          {error && <p className="text-xs text-[var(--xr-danger)]">{error}</p>}

          <Button
            className="w-full"
            size="md"
            type="button"
            onClick={() => void downloadImage()}
            disabled={exporting || !qrDataUrl}
            data-testid="share-download"
          >
            {exporting
              ? locale === 'zh'
                ? '导出中…'
                : 'Exporting…'
              : locale === 'zh'
                ? '下载图片（2× PNG）'
                : 'Download image (2× PNG)'}
          </Button>
        </aside>
      </div>
    </div>
  )
}

interface ShareCardProps {
  route: RoutePublic
  geometry: GeometryResponse | null
  qrDataUrl: string | null
  shareUrl: string
  preset: SharePreset
}

export const ShareCard = forwardRef<HTMLDivElement, ShareCardProps>(function ShareCard(
  { route, geometry, qrDataUrl, shareUrl, preset }: ShareCardProps,
  ref,
) {
  const { locale, t } = useLocale()
  const stats = useMemo(() => {
    if (!geometry) return null
    return trackStats(geometry.line)
  }, [geometry])
  const mapUrl = useMemo(
    () =>
      buildRouteStaticMapUrl(
        geometry ? (geometry.line.length > 0 ? geometry.line : (geometry.points ?? [])) : null,
        {
          variant: 'auto',
          effective: 'light',
          width: 900,
          height: 540,
          padding: 48,
          maxPoints: 160,
          drawLine: geometry ? geometry.line.length > 0 : true,
        },
      ),
    [geometry],
  )
  const footer = t(PRESETS[preset].footerKey)
  const region = [route.region?.province, route.region?.city, route.region?.district]
    .filter(Boolean)
    .join(' · ')
  const translate = t as unknown as TFunction
  const difficulty = route.difficulty
    ? enumDisplay('difficulty', route.difficulty, translate).label
    : '—'
  const statItems = [
    {
      label: locale === 'zh' ? '距离' : 'Distance',
      value: km(route.distance_km ?? stats?.distanceKm),
    },
    {
      label: locale === 'zh' ? '累计爬升' : 'Ascent',
      value: meters(route.ascent_m ?? stats?.ascentM),
    },
    { label: locale === 'zh' ? '难度' : 'Difficulty', value: difficulty },
    {
      label: locale === 'zh' ? '路点' : 'Waypoints',
      value: geometry ? geometry.points.length.toLocaleString() : '—',
    },
  ]

  return (
    <div
      ref={ref}
      className="relative overflow-hidden bg-[var(--xr-bg-canvas)] p-[64px] text-[var(--xr-text-primary)]"
      style={{ width: CARD_W, height: CARD_H }}
      data-testid="share-card"
    >
      <div className="absolute -left-36 -top-36 h-80 w-80 rounded-full bg-[var(--xr-brand-primary-soft)]" />
      <div className="absolute -right-28 top-72 h-72 w-72 rounded-full bg-[var(--xr-info-soft)]" />
      <div className="absolute bottom-20 right-20 h-40 w-40 rounded-full bg-[var(--xr-accent-soft)]" />

      <div className="relative z-10 flex h-full flex-col">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-18 w-18 items-center justify-center rounded-[var(--xr-radius-lg)] bg-[var(--xr-brand-primary)] text-3xl font-black tracking-tight text-[var(--xr-brand-primary-ink)]">
              寻
            </div>
            <div>
              <div className="text-3xl font-black tracking-tight">XingRoute</div>
              <div className="mt-1 text-xl text-[var(--xr-text-secondary)]">寻径</div>
            </div>
          </div>
          <div className="rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-5 py-2 text-lg font-semibold text-[var(--xr-brand-primary)] shadow-[var(--xr-shadow-sm)]">
            {(route.activities ?? [])
              .map((activity) => enumDisplay('activity', activity, translate).label)
              .join(' · ')}
          </div>
        </header>

        <main className="mt-14 flex-1">
          <div className="max-w-[900px]">
            {region && (
              <div className="mb-4 text-2xl font-semibold text-[var(--xr-info)]">{region}</div>
            )}
            <h1
              className="text-[68px] font-black leading-[1.05] tracking-tight"
              style={lineClamp(2)}
            >
              {route.title}
            </h1>
            {route.summary && (
              <p
                className="mt-6 text-[28px] leading-[1.45] text-[var(--xr-text-secondary)]"
                style={lineClamp(3)}
              >
                {route.summary}
              </p>
            )}
          </div>

          <div className="mt-10 overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-md)]">
            <div className="relative h-[540px] overflow-hidden rounded-[var(--xr-radius-lg)] bg-[var(--xr-bg-surface-elev)]">
              {mapUrl ? (
                <img
                  src={mapUrl}
                  crossOrigin="anonymous"
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : route.cover_image_url ? (
                <img
                  src={route.cover_image_url}
                  crossOrigin="anonymous"
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[var(--xr-brand-primary-soft)] text-3xl font-semibold text-[var(--xr-brand-primary)]">
                  {locale === 'zh' ? '暂无线路地图' : 'No route map yet'}
                </div>
              )}
              <div className="absolute left-6 top-6 rounded-full bg-[var(--xr-bg-surface)]/90 px-4 py-2 text-lg font-semibold text-[var(--xr-accent)] shadow-[var(--xr-shadow-sm)]">
                GPX · 2× PNG
              </div>
            </div>
          </div>

          <dl className="mt-8 grid grid-cols-4 gap-4">
            {statItems.map((item) => (
              <div
                key={item.label}
                className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-sm)]"
              >
                <dt className="text-lg font-semibold text-[var(--xr-text-muted)]">{item.label}</dt>
                <dd className="mt-2 truncate text-[32px] font-black tracking-tight">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </main>

        <footer className="mt-10 flex items-end justify-between gap-8">
          <div className="min-w-0 flex-1">
            <div className="text-[26px] font-bold text-[var(--xr-text-primary)]">{footer}</div>
            <div className="mt-3 truncate text-xl text-[var(--xr-text-secondary)]">{shareUrl}</div>
            <div className="mt-5 text-lg font-semibold uppercase tracking-[0.24em] text-[var(--xr-accent)]">
              xingroute.com
            </div>
          </div>
          <div className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-[var(--xr-shadow-md)]">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="" className="h-[210px] w-[210px]" />
            ) : (
              <div className="flex h-[210px] w-[210px] items-center justify-center text-2xl text-[var(--xr-text-muted)]">
                …
              </div>
            )}
            <div className="mt-2 text-center text-base font-semibold text-[var(--xr-text-secondary)]">
              {locale === 'zh' ? '扫码打开' : 'Scan to open'}
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
})

function km(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} km`
}

function meters(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return `${Math.round(value).toLocaleString()} m`
}

function lineClamp(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
  }
}
